import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Hash, Repeat, Clock, Search, ChevronDown, Boxes } from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '@/services/commanServices';
import { AVAILABILITY, UNITS, num, money, catalogToResourceForm, resourceFormToCatalogPayload } from './resourceInject';

const AVAILABILITY_ICONS = { once: Hash, reuse: Repeat, time: Clock };
const normName = (s) => String(s || '').trim().toLowerCase();

// Reusable resource add/edit modal used by the Available Resources widget,
// the Exercise Timeline "Resources" inject group, and (in catalogMode) the
// Resources reference module page.
export default function ResourceFormModal({ draft, entities = [], incident, catalogMode = false, onSave, onClose }) {
  const [form, setForm] = useState(draft);
  // Real incidents: allocation happens AFTER adding, from the action card
  // (link to the whole card or to a specific task) — no allocate field here.
  const isRealIncident = !catalogMode && !incident?.is_exercise && !incident?.is_scenario;
  // Searchable "Allocate to" combobox
  const [allocQuery, setAllocQuery] = useState(draft?.assigneeName || '');
  const [allocOpen, setAllocOpen] = useState(false);

  // Resources reference module (global catalog): when adding an inject, the
  // user can pick an existing catalog resource to prefill the form; a brand-new
  // resource typed here is saved back to the catalog on save.
  const showCatalogPicker = !catalogMode && !draft?.id;
  const [catalog, setCatalog] = useState([]);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (catalogMode || draft?.id) return; // only needed when adding a new inject
    (async () => {
      try {
        const res = await CommanServices.getResourcesCatalog();
        const list = res?.status ? (await res.data) || [] : [];
        setCatalog(Array.isArray(list) ? list.filter((c) => c.is_active !== false) : []);
      } catch (e) { /* catalog is optional — keep the modal usable */ }
    })();
  }, [catalogMode, draft?.id]);

  const pickCatalogItem = (c) => {
    setForm((f) => ({ ...catalogToResourceForm(c), quantity: f.quantity || c.quantity || 1 }));
    setCatalogQuery(c?.name || '');
    setCatalogOpen(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // A new resource created from the inject modal is also saved to the
      // Resources reference module (skipped if the name already exists there).
      if (showCatalogPicker && form.category?.trim()) {
        const exists = catalog.some((c) => normName(c.name) === normName(form.category));
        if (!exists) {
          try {
            const res = await CommanServices.addResourceCatalog(resourceFormToCatalogPayload(form));
            if (res?.status) toast.success('New resource saved to the Resources module too');
          } catch (e) { /* duplicate or offline — the inject itself still saves */ }
        }
      }
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  // No local scroll-lock: the global ModalScrollLock handles every .fixed.inset-0
  // overlay. Locking here too made the global guard snapshot our 'hidden' value
  // and "restore" it on close — freezing the whole page until a hard refresh.

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = num(form.quantity) * num(form.cost);

  const onPickEntity = (val) => {
    if (!val) { setForm((f) => ({ ...f, assigneeType: '', assigneeId: '', assigneeName: '', assigneeParentId: '' })); return; }
    const sep = val.indexOf('::');
    const type = val.slice(0, sep);
    const id = val.slice(sep + 2);
    const ent = entities.find((e) => e.type === type && e.id === id);
    setForm((f) => ({ ...f, assigneeType: type, assigneeId: id, assigneeName: ent?.name || '', assigneeParentId: ent?.parentId || '' }));
  };

  const grouped = useMemo(() => ({
    card: entities.filter((e) => e.type === 'card'),
    task: entities.filter((e) => e.type === 'task'),
  }), [entities]);

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" data-testid="resource-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[460px] max-h-[90vh] overflow-y-auto p-5 space-y-3.5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">
            {catalogMode ? (form.id ? 'Edit resource' : 'Add resource') : (form.id ? 'Edit resource inject' : 'Add resource inject')}
          </h3>
          <button onClick={onClose} data-testid="resource-modal-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>

        {showCatalogPicker && (
          <div className="relative rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="resource-catalog-picker">
            <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
              <Boxes className="h-3.5 w-3.5 text-indigo-500" /> Use an existing resource (Resources module)
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                data-testid="resource-catalog-search"
                value={catalogQuery}
                onChange={(e) => { setCatalogQuery(e.target.value); setCatalogOpen(true); }}
                onFocus={() => setCatalogOpen(true)}
                onBlur={() => setTimeout(() => setCatalogOpen(false), 150)}
                placeholder={catalog.length ? 'Search saved resources — or fill the form below to create a new one' : 'No saved resources yet — fill the form below to create one'}
                autoComplete="off"
                className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white pl-8 pr-8 py-2"
              />
              <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none transition-transform ${catalogOpen ? 'rotate-180' : ''}`} />
            </div>
            {catalogOpen && catalog.length > 0 && (() => {
              const q = catalogQuery.trim().toLowerCase();
              const matches = catalog.filter((c) => !q || String(c.name || '').toLowerCase().includes(q));
              return (
                <div className="absolute z-50 mt-1 left-3 right-3 max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg" data-testid="resource-catalog-list">
                  {matches.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400">No matching resource — it will be created as new.</div>
                  ) : matches.map((c) => {
                    const av = AVAILABILITY[c.availability] || AVAILABILITY.once;
                    return (
                      <button key={c.id} type="button" data-testid={`resource-catalog-item-${c.id}`}
                        onMouseDown={(ev) => ev.preventDefault()} onClick={() => pickCatalogItem(c)}
                        className="w-full flex items-center justify-between text-left px-3 py-2 text-sm text-gray-800 hover:bg-indigo-50">
                        <span className="font-medium">{c.name}</span>
                        <span className="text-[11px] text-gray-400">{av.label} · {money(c.unit_cost)}/unit</span>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            <p className="mt-1.5 text-[10px] text-gray-400">New resources created here are saved to the Resources module automatically.</p>
          </div>
        )}

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Resource type / category</label>
          <input autoFocus value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Ambulance, Fire Truck, Medical Kit" data-testid="resource-category"
            className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2.5 py-2 mt-1" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Quantity</label>
            <input type="number" min="0" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} data-testid="resource-quantity"
              className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2.5 py-2 mt-1" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Unit cost</label>
            <input type="number" min="0" step="0.01" value={form.cost} onChange={(e) => set('cost', e.target.value)} data-testid="resource-cost"
              className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2.5 py-2 mt-1" />
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Availability</label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {Object.entries(AVAILABILITY).map(([k, v]) => {
              const Icon = AVAILABILITY_ICONS[k] || Hash;
              const active = form.availability === k;
              return (
                <button key={k} type="button" data-testid={`resource-availability-${k}`} onClick={() => set('availability', k)}
                  className={`flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2.5 text-[11px] font-bold transition-colors ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  <Icon className="h-4 w-4" /> {v.label}
                </button>
              );
            })}
          </div>
        </div>

        {form.availability === 'time' && (
          <div className="grid grid-cols-2 gap-3 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="resource-time-config">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Replenish every</label>
              <input type="number" min="0" value={form.replenishEvery} onChange={(e) => set('replenishEvery', e.target.value)} placeholder="e.g. 30" data-testid="resource-replenish-every"
                className="w-full text-sm border border-amber-300 rounded-md text-gray-900 bg-white px-2.5 py-2 mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Time unit</label>
              <select value={form.replenishUnit} onChange={(e) => set('replenishUnit', e.target.value)} data-testid="resource-replenish-unit"
                className="w-full text-sm border border-amber-300 rounded-md text-gray-900 bg-white px-2.5 py-2 mt-1">
                {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
          </div>
        )}

        {catalogMode ? null : isRealIncident ? (
          <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5" data-testid="resource-allocation-hint">
            <p className="text-[11px] text-blue-800 leading-relaxed">
              <span className="font-bold">Allocation happens after adding:</span> open the action card and use
              <span className="font-semibold"> Link Resource</span> to attach this resource to the whole card or to a specific task.
              Availability is tracked live as you link.
            </p>
          </div>
        ) : (
        <div className="relative">
          <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Allocate to (action card / task)</label>
          {/* Resources carry no role planning — they're equipment/supplies, not people.
              Searchable combobox: type to filter action cards and tasks, click to pick. */}
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              data-testid="resource-assignee"
              value={allocQuery}
              onChange={(e) => { setAllocQuery(e.target.value); setAllocOpen(true); }}
              onFocus={() => setAllocOpen(true)}
              onBlur={() => setTimeout(() => setAllocOpen(false), 150)}
              placeholder="Search action cards & tasks — or leave unassigned"
              autoComplete="off"
              className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white pl-8 pr-8 py-2"
            />
            <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none transition-transform ${allocOpen ? 'rotate-180' : ''}`} />
          </div>
          {allocOpen && (() => {
            const q = allocQuery.trim().toLowerCase();
            const match = (e) => !q || String(e.name || '').toLowerCase().includes(q);
            const cards = grouped.card.filter(match);
            const tasks = grouped.task.filter(match);
            const pick = (type, e) => {
              onPickEntity(e ? `${type}::${e.id}` : '');
              setAllocQuery(e ? (e.name || '') : '');
              setAllocOpen(false);
            };
            return (
              <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg" data-testid="resource-assignee-list">
                <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => pick(null, null)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50">— Unassigned —</button>
                {cards.length > 0 && (
                  <>
                    <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">Action Cards</div>
                    {cards.map((e) => (
                      <button key={`c${e.id}`} type="button" data-testid={`resource-alloc-card-${e.id}`}
                        onMouseDown={(ev) => ev.preventDefault()} onClick={() => pick('card', e)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-indigo-50">{e.name}</button>
                    ))}
                  </>
                )}
                {tasks.length > 0 && (
                  <>
                    <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">Tasks</div>
                    {tasks.map((e) => (
                      <button key={`tk${e.id}`} type="button" data-testid={`resource-alloc-task-${e.id}`}
                        onMouseDown={(ev) => ev.preventDefault()} onClick={() => pick('task', e)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-teal-50">{e.name}</button>
                    ))}
                  </>
                )}
                {cards.length === 0 && tasks.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-400">{q ? 'No matching action cards or tasks.' : 'No action cards available yet.'}</div>
                )}
              </div>
            );
          })()}
        </div>
        )}

        <div className="flex items-center justify-between rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2.5">
          <span className="text-xs font-bold uppercase tracking-wider text-indigo-500">Total cost</span>
          <span className="text-lg font-black text-indigo-700" data-testid="resource-total-preview">{money(total)}</span>
        </div>

        <button onClick={handleSave} disabled={saving} data-testid="resource-save"
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-bold py-2.5 rounded-md transition-colors">
          {saving ? 'Saving…' : form.id ? 'Save changes' : 'Add resource'}
        </button>
      </div>
    </div>,
    document.body
  );
}
